import React, { useEffect, useRef, useState } from "react";
import {
  Application,
  Assets,
  Container,
  Graphics,
  Rectangle,
  Sprite,
  Text,
  TextStyle,
} from "pixi.js";
import { WORLD_BUILDINGS } from "../config/worldMapLinks";

const WORLD_TEXTURE_PATH = "/swiat.png";
const PLAYER_TEXTURE_PATH = "/hood.png";
const CARPET_TEXTURE_PATH = "/dywan.png";
const WORLD_SCALE = 3;
const MOBILE_BREAKPOINT = 900;
const DESKTOP_INITIAL_ZOOM = 0.72;
const MOBILE_INITIAL_ZOOM = 0.2;
const DESKTOP_MIN_ZOOM = 0.32;
const MOBILE_MIN_ZOOM = 0.12;
const MAX_ZOOM = 2.3;
const PLAYER_START_X_RATIO = 0.31;
const PLAYER_START_Y_RATIO = 0.27;
const PLAYER_HEIGHT = 520;
const CARPET_DISPLAY_WIDTH = 330;
const EDGE_BUTTON_SIZE = 72;
const EDGE_BUTTON_MARGIN = 58;
const EDGE_MOVE_STEP = 240;
const BUILDING_ENTRY_DISTANCE = 24;
const MAGIC_TRANSITION_MS = 1100;

const ChessWorldPixi = () => {
  const containerRef = useRef(null);
  const buildingActionRef = useRef(() => {});
  const [magicTransition, setMagicTransition] = useState({
    active: false,
    label: "",
  });
  const [gpsMenuOpen, setGpsMenuOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    let cleanupRequested = false;
    let navigationLocked = false;
    let transitionTimeoutId = null;
    let app = null;
    let appInitialized = false;
    let appDestroyed = false;
    let cleanupResize = () => {};
    let cleanupWheel = () => {};
    let cleanupPointerDown = () => {};
    let cleanupTicker = () => {};

    const destroyApp = () => {
      cleanupResize();
      cleanupResize = () => {};

      cleanupWheel();
      cleanupWheel = () => {};

      cleanupPointerDown();
      cleanupPointerDown = () => {};

      cleanupTicker();
      cleanupTicker = () => {};

      if (transitionTimeoutId) {
        window.clearTimeout(transitionTimeoutId);
        transitionTimeoutId = null;
      }

      buildingActionRef.current = () => {};

      if (!app || !appInitialized || appDestroyed) {
        return;
      }

      appDestroyed = true;
      app.destroy(true, {
        children: true,
        texture: false,
      });
    };

    const setup = async () => {
      const getZoomProfile = () => {
        const isMobileViewport =
          window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches ||
          window.matchMedia("(pointer: coarse)").matches;

        return isMobileViewport
          ? {
              initialZoom: MOBILE_INITIAL_ZOOM,
              minZoom: MOBILE_MIN_ZOOM,
            }
          : {
              initialZoom: DESKTOP_INITIAL_ZOOM,
              minZoom: DESKTOP_MIN_ZOOM,
            };
      };

      const nextApp = new Application();
      app = nextApp;

      try {
        await nextApp.init({
          resizeTo: window,
          background: "#111827",
          antialias: true,
        });
      } catch (error) {
        if (!cleanupRequested) {
          console.error("Failed to initialize ChessWorldPixi", error);
        }
        return;
      }

      appInitialized = true;

      if (
        cleanupRequested ||
        !mounted ||
        !containerRef.current ||
        app !== nextApp
      ) {
        destroyApp();
        return;
      }

      containerRef.current.innerHTML = "";
      containerRef.current.appendChild(nextApp.canvas);

      const camera = new Container();
      const world = new Container();
      const ui = new Container();
      const edgeControls = new Container();

      nextApp.stage.addChild(camera);
      camera.addChild(world);
      nextApp.stage.addChild(ui);
      ui.addChild(edgeControls);

      const mapTexture = await Assets.load(WORLD_TEXTURE_PATH);
      const worldWidth = mapTexture.width * WORLD_SCALE;
      const worldHeight = mapTexture.height * WORLD_SCALE;
      const worldBuildings = WORLD_BUILDINGS.map((building) => ({
        ...building,
        bounds: {
          xMin: worldWidth * building.boundsRatios.xMin,
          xMax: worldWidth * building.boundsRatios.xMax,
          yMin: worldHeight * building.boundsRatios.yMin,
          yMax: worldHeight * building.boundsRatios.yMax,
        },
        entryPoint: {
          x: worldWidth * building.entryRatios.x,
          y: worldHeight * building.entryRatios.y,
        },
      }));
      const mapSprite = new Sprite(mapTexture);
      mapSprite.width = worldWidth;
      mapSprite.height = worldHeight;
      world.addChild(mapSprite);

      const overlay = new Graphics();
      overlay.rect(0, 0, worldWidth, worldHeight).fill({
        color: 0x000000,
        alpha: 0.03,
      });
      world.addChild(overlay);

      const titleStyle = new TextStyle({
        fontFamily: "Arial",
        fontSize: 26,
        fill: "#ffffff",
        stroke: "#000000",
        strokeThickness: 4,
        fontWeight: "bold",
      });

      const smallStyle = new TextStyle({
        fontFamily: "Arial",
        fontSize: 18,
        fill: "#ffffff",
        stroke: "#000000",
        strokeThickness: 3,
      });

      const edgeButtonStyle = new TextStyle({
        fontFamily: "Arial",
        fontSize: 34,
        fill: "#ffffff",
        fontWeight: "bold",
      });

      const infoText = new Text({
        text: "Click on the world to travel",
        style: titleStyle,
      });
      infoText.x = 20;
      infoText.y = 18;
      ui.addChild(infoText);

      const subText = new Text({
        text: "Scroll to zoom. Explore the overworld.",
        style: smallStyle,
      });
      subText.x = 20;
      subText.y = 56;
      ui.addChild(subText);

      const uiBg = new Graphics();
      uiBg.roundRect(10, 10, 420, 76, 14).fill({
        color: 0x000000,
        alpha: 0.35,
      });
      ui.addChildAt(uiBg, 0);

      const player = new Container();
      world.addChild(player);

      const shadow = new Graphics();
      shadow.ellipse(0, 88, 66, 20).fill({
        color: 0x000000,
        alpha: 0.22,
      });
      player.addChild(shadow);

      const carpet = new Container();
      carpet.y = 110;
      player.addChild(carpet);

      let carpetSprite = null;

      try {
        const carpetTexture = await Assets.load(CARPET_TEXTURE_PATH);
        carpetSprite = new Sprite(carpetTexture);
        carpetSprite.anchor.set(0.5, 0.62);
        carpetSprite.width = CARPET_DISPLAY_WIDTH;
        carpetSprite.scale.y = carpetSprite.scale.x;
        carpet.addChild(carpetSprite);
      } catch {
        const carpetBody = new Graphics();
        carpetBody.roundRect(-120, -26, 240, 52, 18).fill({
          color: 0x8b1e3f,
        });
        carpetBody.stroke({
          color: 0xf8d67a,
          width: 4,
        });
        carpet.addChild(carpetBody);
      }

      let playerSprite = null;

      try {
        const playerTexture = await Assets.load(PLAYER_TEXTURE_PATH);
        playerSprite = new Sprite(playerTexture);
        playerSprite.anchor.set(0.5, 0.82);
        playerSprite.y = 18;
        playerSprite.scale.set(PLAYER_HEIGHT / playerTexture.height);
        player.addChild(playerSprite);
      } catch {
        const fallbackBody = new Graphics();
        fallbackBody.roundRect(-26, -26, 52, 52, 12).fill({
          color: 0x1d4ed8,
        });
        fallbackBody.stroke({
          color: 0x111111,
          width: 3,
        });
        player.addChild(fallbackBody);
      }

      const playerStartX = worldWidth * PLAYER_START_X_RATIO;
      const playerStartY = worldHeight * PLAYER_START_Y_RATIO;

      player.x = playerStartX;
      player.y = playerStartY;

      const playerState = {
        x: playerStartX,
        y: playerStartY,
      };

      const moveState = {
        active: false,
        buildingTarget: null,
        startX: playerStartX,
        startY: playerStartY,
        targetX: playerStartX,
        targetY: playerStartY,
        totalDistance: 0,
        traveled: 0,
        dirX: 0,
        dirY: 0,
      };

      const initialZoomProfile = getZoomProfile();
      let currentZoom = initialZoomProfile.initialZoom;
      let targetZoom = initialZoomProfile.initialZoom;
      let minZoom = initialZoomProfile.minZoom;
      const maxZoom = MAX_ZOOM;

      const cameraState = {
        x: 0,
        y: 0,
      };

      const zoomFocusState = {
        active: false,
        worldX: 0,
        worldY: 0,
        screenX: 0,
        screenY: 0,
        releaseAt: 0,
      };

      function clamp(value, min, max) {
        return Math.max(min, Math.min(max, value));
      }

      function clampWorldPoint(x, y) {
        return {
          x: clamp(x, 24, worldWidth - 24),
          y: clamp(y, 24, worldHeight - 24),
        };
      }

      function getViewportSize() {
        return {
          width: nextApp.screen.width || window.innerWidth,
          height: nextApp.screen.height || window.innerHeight,
        };
      }

      function clampCameraPosition(x, y, zoomValue) {
        const { width: viewportWidth, height: viewportHeight } =
          getViewportSize();
        const scaledWorldWidth = worldWidth * zoomValue;
        const scaledWorldHeight = worldHeight * zoomValue;

        if (scaledWorldWidth <= viewportWidth) {
          x = (viewportWidth - scaledWorldWidth) / 2;
        } else {
          const minCamX = viewportWidth - scaledWorldWidth;
          x = clamp(x, minCamX, 0);
        }

        if (scaledWorldHeight <= viewportHeight) {
          y = (viewportHeight - scaledWorldHeight) / 2;
        } else {
          const minCamY = viewportHeight - scaledWorldHeight;
          y = clamp(y, minCamY, 0);
        }

        return { x, y };
      }

      function getProgress() {
        if (!moveState.active || moveState.totalDistance <= 0) return 1;
        return clamp(moveState.traveled / moveState.totalDistance, 0, 1);
      }

      function getDistance(ax, ay, bx, by) {
        return Math.hypot(bx - ax, by - ay);
      }

      function isInsideRect(x, y, rect) {
        return (
          x >= rect.xMin && x <= rect.xMax && y >= rect.yMin && y <= rect.yMax
        );
      }

      function openWorldLink(href) {
        window.location.assign(href);
      }

      function getBuildingAtWorldPoint(x, y) {
        return (
          worldBuildings.find((building) =>
            isInsideRect(x, y, building.bounds),
          ) || null
        );
      }

      function beginMagicTransition(building) {
        if (navigationLocked) {
          return;
        }

        navigationLocked = true;
        moveState.active = false;
        moveState.buildingTarget = null;
        infoText.text = `Opening ${building.label}...`;
        subText.text = building.href;
        setMagicTransition({
          active: true,
          label: building.label,
        });

        transitionTimeoutId = window.setTimeout(() => {
          openWorldLink(building.href);
        }, MAGIC_TRANSITION_MS);
      }

      function queueBuildingEntry(building) {
        if (navigationLocked) {
          return;
        }

        moveState.buildingTarget = building;
        infoText.text = `Heading to ${building.label}...`;
        subText.text = "Approaching entrance";
        startMove(building.entryPoint.x, building.entryPoint.y, {
          keepBuildingTarget: true,
        });
      }

      function maybeEnterAutoBuilding() {
        if (navigationLocked) {
          return;
        }

        if (moveState.buildingTarget) {
          if (
            !moveState.active &&
            getDistance(
              playerState.x,
              playerState.y,
              moveState.buildingTarget.entryPoint.x,
              moveState.buildingTarget.entryPoint.y,
            ) <= BUILDING_ENTRY_DISTANCE
          ) {
            beginMagicTransition(moveState.buildingTarget);
          }
          return;
        }

        const autoEnterBuilding = worldBuildings.find(
          (building) =>
            building.autoEnter &&
            isInsideRect(playerState.x, playerState.y, building.bounds),
        );

        if (!autoEnterBuilding) {
          return;
        }

        beginMagicTransition(autoEnterBuilding);
      }

      function getSpeedMultiplier(progress) {
        const minFactor = 0.22;
        const maxFactor = 1.55;

        const curve = Math.sin(progress * Math.PI);
        return minFactor + (maxFactor - minFactor) * curve;
      }

      function updatePlayerVisual() {
        player.x = playerState.x;
        player.y = playerState.y;

        const p = getProgress();
        const flightWave = performance.now() / 180;
        const hoverLift = 18 + Math.sin(flightWave) * 4;
        const motionBounce = moveState.active
          ? Math.sin(p * Math.PI * 8) * 4
          : Math.sin(flightWave * 0.9) * 1.6;
        const flightTilt = moveState.active
          ? clamp(moveState.dirX * 0.14, -0.14, 0.14)
          : Math.sin(flightWave * 0.7) * 0.02;
        const squash = moveState.active
          ? 1 + Math.sin(p * Math.PI * 8) * 0.01
          : 1;

        player.y = playerState.y - hoverLift + motionBounce;
        player.scale.set(squash, 1 / squash);
        carpet.rotation = flightTilt;
        carpet.y = 78 + Math.sin(flightWave * 1.25) * 3;
        shadow.scale.set(1 - hoverLift * 0.0035, 1 - hoverLift * 0.004);
        shadow.alpha = 0.22 - hoverLift * 0.002;

        if (playerSprite) {
          playerSprite.rotation = -flightTilt * 0.35;
        }
      }

      function getPlayerCameraTarget(zoomValue = currentZoom) {
        const { width: viewportWidth, height: viewportHeight } =
          getViewportSize();

        return clampCameraPosition(
          viewportWidth / 2 - playerState.x * zoomValue,
          viewportHeight / 2 - playerState.y * zoomValue,
          zoomValue,
        );
      }

      function getZoomFocusCameraTarget(zoomValue = currentZoom) {
        return clampCameraPosition(
          zoomFocusState.screenX - zoomFocusState.worldX * zoomValue,
          zoomFocusState.screenY - zoomFocusState.worldY * zoomValue,
          zoomValue,
        );
      }

      function updateCamera() {
        world.scale.set(currentZoom);
        const zoomIsSettling = Math.abs(targetZoom - currentZoom) > 0.002;
        const focusStillFresh = performance.now() < zoomFocusState.releaseAt;
        const useZoomFocus =
          zoomFocusState.active && (zoomIsSettling || focusStillFresh);
        const targetCam = useZoomFocus
          ? getZoomFocusCameraTarget(currentZoom)
          : getPlayerCameraTarget(currentZoom);
        const cameraLerp = useZoomFocus ? 0.22 : 0.14;

        if (!useZoomFocus) {
          zoomFocusState.active = false;
        }

        cameraState.x += (targetCam.x - cameraState.x) * cameraLerp;
        cameraState.y += (targetCam.y - cameraState.y) * cameraLerp;

        camera.x = cameraState.x;
        camera.y = cameraState.y;
      }

      function screenToWorld(screenX, screenY, zoomValue = currentZoom) {
        return {
          x: (screenX - cameraState.x) / zoomValue,
          y: (screenY - cameraState.y) / zoomValue,
        };
      }

      function startMove(worldX, worldY, options = {}) {
        if (!options.keepBuildingTarget) {
          moveState.buildingTarget = null;
        }

        const target = clampWorldPoint(worldX, worldY);

        const dx = target.x - playerState.x;
        const dy = target.y - playerState.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < 1) return;

        moveState.active = true;
        moveState.startX = playerState.x;
        moveState.startY = playerState.y;
        moveState.targetX = target.x;
        moveState.targetY = target.y;
        moveState.totalDistance = dist;
        moveState.traveled = 0;
        moveState.dirX = dx / dist;
        moveState.dirY = dy / dist;

        infoText.text = "Moving...";
        subText.text = `Target: ${Math.round(target.x)}, ${Math.round(target.y)}`;
      }

      function moveByOffset(offsetX, offsetY) {
        startMove(
          playerState.x + offsetX * EDGE_MOVE_STEP,
          playerState.y + offsetY * EDGE_MOVE_STEP,
        );
      }

      function createEdgeButton(label, onTap) {
        const button = new Container();
        const background = new Graphics();
        const idleAlpha = 0.22;
        const hoverAlpha = 0.56;
        const activeAlpha = 0.88;

        background
          .roundRect(
            -EDGE_BUTTON_SIZE / 2,
            -EDGE_BUTTON_SIZE / 2,
            EDGE_BUTTON_SIZE,
            EDGE_BUTTON_SIZE,
            20,
          )
          .fill({
            color: 0x111827,
            alpha: idleAlpha,
          });
        background.stroke({
          color: 0xffffff,
          width: 2,
        });
        button.addChild(background);

        const labelText = new Text({
          text: label,
          style: edgeButtonStyle,
        });
        labelText.anchor.set(0.5);
        labelText.alpha = 0.8;
        button.addChild(labelText);

        const setVisualState = (state) => {
          if (state === "active") {
            background.alpha = activeAlpha;
            labelText.alpha = 1;
            button.scale.set(1.06);
            return;
          }

          if (state === "hover") {
            background.alpha = hoverAlpha;
            labelText.alpha = 0.98;
            button.scale.set(1.03);
            return;
          }

          background.alpha = idleAlpha;
          labelText.alpha = 0.8;
          button.scale.set(1);
        };

        button.eventMode = "static";
        button.cursor = "pointer";
        button.hitArea = new Rectangle(
          -EDGE_BUTTON_SIZE / 2,
          -EDGE_BUTTON_SIZE / 2,
          EDGE_BUTTON_SIZE,
          EDGE_BUTTON_SIZE,
        );

        button.on("pointerdown", (event) => {
          event.stopPropagation();
          setVisualState("active");
        });

        button.on("pointerup", (event) => {
          event.stopPropagation();
          setVisualState("hover");
        });

        button.on("pointerupoutside", () => {
          setVisualState("idle");
        });

        button.on("pointerover", () => {
          setVisualState("hover");
        });

        button.on("pointerout", () => {
          setVisualState("idle");
        });

        button.on("pointertap", (event) => {
          event.stopPropagation();
          onTap();
        });

        setVisualState("idle");

        return button;
      }

      const edgeButtons = [
        {
          button: createEdgeButton("\u2196", () =>
            moveByOffset(-Math.SQRT1_2, -Math.SQRT1_2),
          ),
          getPosition: (width, height) => ({
            x: EDGE_BUTTON_MARGIN,
            y: EDGE_BUTTON_MARGIN,
          }),
        },
        {
          button: createEdgeButton("\u2191", () => moveByOffset(0, -1)),
          getPosition: (width) => ({
            x: width / 2,
            y: EDGE_BUTTON_MARGIN,
          }),
        },
        {
          button: createEdgeButton("\u2197", () =>
            moveByOffset(Math.SQRT1_2, -Math.SQRT1_2),
          ),
          getPosition: (width) => ({
            x: width - EDGE_BUTTON_MARGIN,
            y: EDGE_BUTTON_MARGIN,
          }),
        },
        {
          button: createEdgeButton("\u2190", () => moveByOffset(-1, 0)),
          getPosition: (width, height) => ({
            x: EDGE_BUTTON_MARGIN,
            y: height / 2,
          }),
        },
        {
          button: createEdgeButton("\u2192", () => moveByOffset(1, 0)),
          getPosition: (width, height) => ({
            x: width - EDGE_BUTTON_MARGIN,
            y: height / 2,
          }),
        },
        {
          button: createEdgeButton("\u2199", () =>
            moveByOffset(-Math.SQRT1_2, Math.SQRT1_2),
          ),
          getPosition: (width, height) => ({
            x: EDGE_BUTTON_MARGIN,
            y: height - EDGE_BUTTON_MARGIN,
          }),
        },
        {
          button: createEdgeButton("\u2193", () => moveByOffset(0, 1)),
          getPosition: (width, height) => ({
            x: width / 2,
            y: height - EDGE_BUTTON_MARGIN,
          }),
        },
        {
          button: createEdgeButton("\u2198", () =>
            moveByOffset(Math.SQRT1_2, Math.SQRT1_2),
          ),
          getPosition: (width, height) => ({
            x: width - EDGE_BUTTON_MARGIN,
            y: height - EDGE_BUTTON_MARGIN,
          }),
        },
      ];

      edgeButtons.forEach(({ button }) => {
        edgeControls.addChild(button);
      });

      buildingActionRef.current = (buildingId) => {
        const building = worldBuildings.find((item) => item.id === buildingId);

        if (building) {
          queueBuildingEntry(building);
        }
      };

      function layoutEdgeButtons() {
        const { width: viewportWidth, height: viewportHeight } =
          getViewportSize();

        edgeButtons.forEach(({ button, getPosition }) => {
          const nextPosition = getPosition(viewportWidth, viewportHeight);
          button.x = nextPosition.x;
          button.y = nextPosition.y;
        });
      }

      nextApp.stage.eventMode = "static";
      nextApp.stage.hitArea = nextApp.screen;

      const handlePointerDown = (event) => {
        const point = screenToWorld(event.global.x, event.global.y);
        const clickedBuilding = getBuildingAtWorldPoint(point.x, point.y);

        if (clickedBuilding) {
          queueBuildingEntry(clickedBuilding);
          return;
        }

        startMove(point.x, point.y);
      };

      nextApp.stage.on("pointerdown", handlePointerDown);

      cleanupPointerDown = () => {
        nextApp.stage.off("pointerdown", handlePointerDown);
      };

      const handleWheel = (event) => {
        event.preventDefault();

        const rect = nextApp.canvas.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;

        const beforeZoomPoint = screenToWorld(mouseX, mouseY, currentZoom);

        const zoomStep = event.deltaY < 0 ? 1.06 : 0.94;
        targetZoom = clamp(targetZoom * zoomStep, minZoom, maxZoom);
        zoomFocusState.active = true;
        zoomFocusState.worldX = beforeZoomPoint.x;
        zoomFocusState.worldY = beforeZoomPoint.y;
        zoomFocusState.screenX = mouseX;
        zoomFocusState.screenY = mouseY;
        zoomFocusState.releaseAt = performance.now() + 160;
      };

      nextApp.canvas.addEventListener("wheel", handleWheel, { passive: false });

      cleanupWheel = () => {
        nextApp.canvas.removeEventListener("wheel", handleWheel);
      };

      const handleResize = () => {
        const nextZoomProfile = getZoomProfile();
        minZoom = nextZoomProfile.minZoom;
        currentZoom = clamp(currentZoom, minZoom, maxZoom);
        targetZoom = clamp(targetZoom, minZoom, maxZoom);
        zoomFocusState.active = false;
        layoutEdgeButtons();
        updateCamera();
      };

      window.addEventListener("resize", handleResize);

      cleanupResize = () => {
        window.removeEventListener("resize", handleResize);
      };

      const tick = (ticker) => {
        const deltaMS = ticker.deltaMS;
        const dt = deltaMS / 1000;

        currentZoom += (targetZoom - currentZoom) * 0.14;

        if (moveState.active) {
          const progress = getProgress();
          const speedMultiplier = getSpeedMultiplier(progress);

          const baseSpeed = 900;
          const currentSpeed = baseSpeed * speedMultiplier;
          const step = currentSpeed * dt;

          const remaining = moveState.totalDistance - moveState.traveled;
          const moveAmount = Math.min(step, remaining);

          playerState.x += moveState.dirX * moveAmount;
          playerState.y += moveState.dirY * moveAmount;
          moveState.traveled += moveAmount;

          if (moveState.traveled >= moveState.totalDistance - 0.001) {
            playerState.x = moveState.targetX;
            playerState.y = moveState.targetY;
            moveState.active = false;
            moveState.traveled = moveState.totalDistance;

            infoText.text = "Click on the world to travel";
            subText.text = "Scroll to zoom. Explore the overworld.";
          }
        }

        updatePlayerVisual();
        maybeEnterAutoBuilding();
        updateCamera();
      };

      nextApp.ticker.add(tick);

      cleanupTicker = () => {
        nextApp.ticker.remove(tick);
      };

      updatePlayerVisual();
      maybeEnterAutoBuilding();
      layoutEdgeButtons();
      updateCamera();
    };

    setup();

    return () => {
      mounted = false;
      cleanupRequested = true;
      destroyApp();
    };
  }, []);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100vh",
        overflow: "hidden",
        background: "#111827",
      }}
    >
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
          overflow: "hidden",
          background: "#111827",
        }}
      />

      <div
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 3,
          pointerEvents: magicTransition.active ? "auto" : "none",
          opacity: magicTransition.active ? 1 : 0,
          transition: "opacity 240ms ease",
          background:
            "radial-gradient(circle at center, rgba(89, 0, 20, 0.18) 0%, rgba(10, 7, 25, 0.42) 32%, rgba(0, 0, 0, 0.9) 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: magicTransition.active ? 280 : 120,
            height: magicTransition.active ? 280 : 120,
            borderRadius: "50%",
            border: "2px solid rgba(194, 148, 255, 0.65)",
            boxShadow:
              "0 0 30px rgba(159, 90, 253, 0.36), inset 0 0 50px rgba(0, 0, 0, 0.45)",
            background:
              "radial-gradient(circle, rgba(160, 87, 255, 0.28) 0%, rgba(16, 16, 35, 0.3) 48%, rgba(0, 0, 0, 0) 72%)",
            transform: magicTransition.active ? "scale(1)" : "scale(0.5)",
            transition:
              "transform 900ms cubic-bezier(0.2, 0.8, 0.2, 1), width 900ms ease, height 900ms ease",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(6px)",
          }}
        >
          <div
            style={{
              textAlign: "center",
              color: "#ffffff",
              textShadow: "0 0 18px rgba(167, 110, 255, 0.7)",
            }}
          >
            <div
              style={{
                fontSize: 14,
                letterSpacing: 4,
                textTransform: "uppercase",
                opacity: 0.78,
              }}
            >
              Entering
            </div>
            <div
              style={{
                marginTop: 8,
                fontSize: 22,
                fontWeight: 800,
              }}
            >
              {magicTransition.label}
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label={gpsMenuOpen ? "Close map GPS" : "Open map GPS"}
        onClick={() => setGpsMenuOpen((open) => !open)}
        style={{
          position: "absolute",
          top: 16,
          right: 16,
          zIndex: 4,
          width: 108,
          height: 108,
          padding: 0,
          border: "none",
          background: "transparent",
          cursor: "pointer",
        }}
      >
        <img
          src="/gps.png"
          alt="GPS"
          style={{
            width: "100%",
            height: "100%",
            display: "block",
            filter: gpsMenuOpen
              ? "drop-shadow(0 0 16px rgba(255, 196, 0, 0.45))"
              : "drop-shadow(0 8px 18px rgba(0,0,0,0.34))",
          }}
        />
      </button>

      <div
        style={{
          position: "absolute",
          top: 132,
          right: 16,
          zIndex: 2,
          width: "min(270px, calc(100vw - 32px))",
          maxHeight: "calc(100vh - 148px)",
          overflowY: "auto",
          padding: 12,
          borderRadius: 16,
          background: "rgba(10, 15, 25, 0.82)",
          border: "1px solid rgba(255, 255, 255, 0.14)",
          boxShadow: "0 12px 34px rgba(0, 0, 0, 0.28)",
          backdropFilter: "blur(10px)",
          opacity: gpsMenuOpen ? 1 : 0,
          transform: gpsMenuOpen ? "translateY(0)" : "translateY(-12px)",
          pointerEvents: gpsMenuOpen ? "auto" : "none",
          transition: "opacity 180ms ease, transform 180ms ease",
        }}
      >
        <div
          style={{
            color: "#ffffff",
            fontSize: 16,
            fontWeight: 800,
            marginBottom: 8,
          }}
        >
          Map GPS
        </div>

        <div
          style={{
            color: "rgba(255,255,255,0.7)",
            fontSize: 12,
            lineHeight: 1.4,
            marginBottom: 10,
          }}
        >
          Linki i adresy edytujesz w `src/config/worldMapLinks.js`.
        </div>

        {WORLD_BUILDINGS.map((building) => (
          <button
            key={building.id}
            type="button"
            onClick={() => {
              setGpsMenuOpen(false);
              buildingActionRef.current(building.id);
            }}
            style={{
              display: "block",
              width: "100%",
              marginBottom: 8,
              padding: "10px 12px",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(255,255,255,0.08)",
              color: "#ffffff",
              textAlign: "left",
              cursor: "pointer",
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 14 }}>
              {building.menuLabel}
            </div>
            <div
              style={{
                marginTop: 2,
                fontSize: 11,
                color: "rgba(255,255,255,0.66)",
                wordBreak: "break-all",
              }}
            >
              {building.href}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default ChessWorldPixi;
